import type { ItemFiltersType } from '../api/types/itemFilters';
import type {
  ItemAggregationRequest,
  ItemAggregationSpec,
  ItemRequestModelField,
} from '../api/types/items';

const normalizeAggregations = (aggregations: ItemAggregationSpec[]): ItemAggregationSpec[] => {
  return [...aggregations].sort((a, b) => {
    if (a.field !== b.field) return a.field.localeCompare(b.field);
    if (a.function !== b.function) return a.function.localeCompare(b.function);
    return (a.direction ?? '').localeCompare(b.direction ?? '');
  });
};

export const commonQueryKeys = {
  loops: {
    all: () => ['loops'] as const,
  },
  boxes: {
    all: ({ filter }: { filter?: string } = {}) => ['boxes', { filter }],
    allById: () => ['boxes', 'byId'],
    details: (boxId: number) => ['boxes', 'details', boxId],
  },
  customFields: {
    all: () => ['customFields'],
    search: (search: string) => ['customFields', search],
    details: (id: number) => [...commonQueryKeys.customFields.all(), 'details', id],
    picklistOptions: (customFieldId: number) => [
      ...commonQueryKeys.customFields.details(customFieldId),
      'picklistOptions',
      customFieldId,
    ],
  },
  estimationSchemes: {
    all: () => ['estimationSchemes'],
    details: (id: number) => [...commonQueryKeys.estimationSchemes.all(), 'details', id],
    estimations: ['estimationSchemes', 'estimations'],
  },
  itemTypes: {
    all: ['itemTypes'],
    fields: (itemTypeId: number) => ['itemTypes', itemTypeId, 'fields'],
  },
  itemTypeLevels: {
    all: ['itemTypeLevels'],
    create: ['itemTypeLevels', 'create'],
    delete: ['itemTypeLevels', 'delete'],
    details: (levelId: number) => ['itemTypeLevels', 'details', levelId],
  },
  items: {
    all: () => ['items'] as const,
    byFilters: ({
      filter,
      limit,
      order,
      hierarchical,
    }: {
      filter?: ItemFiltersType;
      limit?: number;
      order?: string;
      hierarchical?: boolean;
    } = {}) => ['items', { filter, limit, order, hierarchical }],
    aggregations: (request: ItemAggregationRequest) => [
      'items',
      'aggregations',
      { ...request, aggregations: normalizeAggregations(request.aggregations) },
    ],
    aggregationSingle: (itemId: number, aggregations: ItemAggregationSpec[]) =>
      ['items', 'aggregations', 'single', itemId, normalizeAggregations(aggregations)] as const,
  },
  itemsDataSet: {
    all: () => ['itemsDataSet'] as const,
    byFilter: (filter?: ItemFiltersType) => ['itemsDataSet', filter],
  },
  itemsTreeSet: {
    all: () => ['itemsTreeSet'] as const,
    byFilter: (filter?: ItemFiltersType) => ['itemsTreeSet', filter],
  },
  insightsItemsDataSet: (filters?: ItemFiltersType) =>
    filters && Object.keys(filters).length > 0
      ? ['insightsItemsDataSet', { filters }]
      : ['insightsItemsDataSet'],
  itemsBulkFetch: ({
    ids,
    fields,
    filter,
  }: {
    ids: number[];
    fields?: ItemRequestModelField[];
    filter?: ItemFiltersType;
  }) => [
    'itemsBulkFetch',
    {
      ids,
      ...(fields ? { fields } : {}),
      ...(filter ? { filter } : {}),
    },
  ],
  item: (itemId: number) => ['item', itemId],
  itemAncestors: (itemId: number) => ['itemAncestors', itemId],
  itemDescendants: {
    all: () => ['itemDescendants'] as const,
    byItemIds: (itemIds: number[]) => ['itemDescendants', [...itemIds].sort((a, b) => a - b)],
    byIds: (itemIds: number[]) => ['itemDescendants', 'by-ids', [...itemIds].sort((a, b) => a - b)],
    single: (itemId: number) => ['itemDescendants', 'single', itemId] as const,
  },
  itemStatuses: {
    all: () => ['itemStatuses'],
    detail: (itemStatusId: number) => [
      ...commonQueryKeys.itemStatuses.all(),
      'detail',
      itemStatusId,
    ],
  },
  spmCustomFields: {
    all: () => ['spmCustomFields'],
  },
  loggedWorkSummary: (itemId: number) => ['loggedWorkSummary', itemId],
};
